import { QuantityAdjustment } from '../../graphQL/queries/getSession/types'
import { IRoundingRules, ROUNDING_RULE_TYPES, RoundingRuleType } from '../../interfaces'
import { isRuleInvalidatedBy, QuantityAdjustmentReason } from './normalizeQuantity'

/** The numbers an alert template can name. */
export type QuantityPlaceholder = 'max' | 'min'

const PLACEHOLDER_PATTERNS: Record<QuantityPlaceholder, RegExp> = {
  max: /\{\{\s*max\s*\}\}/g,
  min: /\{\{\s*min\s*\}\}/g,
}

/** A configured text, or `undefined` when it is absent, blank or not a string. */
const configuredText = (template: unknown): string | undefined =>
  typeof template === 'string' && template.trim() !== '' ? template : undefined

/**
 * Resolves a quantity-alert template that names one number.
 *
 * The number is per product (and for the ceiling, per customer), so it cannot
 * live in the translated string. The template carries a `{{placeholder}}` and
 * this fills it in. A template without the placeholder is returned as written,
 * so a storefront can word the message without naming the number.
 *
 * An absent or empty template is not a switch. It means there is nothing to
 * say, and the caller falls back to the alert it showed before the message
 * existed, so a storefront that never configures one behaves as it always did.
 *
 * The number is in the cart's own units. On a storefront with
 * `globals.counter.modifierMap` the counter shows a different unit, so a named
 * number can disagree with the screen there — as `{{max}}` already could.
 *
 * @param template The configured text, if any.
 * @param placeholder Which placeholder to fill.
 * @param value The number to substitute.
 * @returns The message to show, or `undefined` when there is none to show.
 */
export const formatQuantityMessage = (
  template: string | undefined,
  placeholder: QuantityPlaceholder,
  value: number | undefined,
): string | undefined => {
  const text = configuredText(template)
  if (text === undefined || typeof value !== 'number' || !Number.isFinite(value)) {
    return undefined
  }
  return text.replace(PLACEHOLDER_PATTERNS[placeholder], String(value))
}

/** The per-rule alert texts, as configured under `texts.cart`. */
export interface IRuleQuantityMessages {
  minQuantityMessage?: string
  layerQuantityMessage?: string
  palletQuantityMessage?: string
}

interface IResolveRuleQuantityMessage {
  reason: QuantityAdjustmentReason
  quantityAdjustment?: `${QuantityAdjustment}`
  /** The breached rule as the counter reported it; any shape may arrive. */
  ruleInvalidatedBy?: unknown
  minQty?: number | null
  /** The quantity that will be committed. */
  committedValue: number
  messages: IRuleQuantityMessages
}

type TypedRoundingRule = IRoundingRules & { type: RoundingRuleType }

const isTypedRoundingRule = (maybeRule: unknown): maybeRule is TypedRoundingRule =>
  isRuleInvalidatedBy(maybeRule) &&
  (ROUNDING_RULE_TYPES as readonly unknown[]).includes(maybeRule.type)

/** A record, so a new rule type does not compile until it has its own text. */
const RULE_MESSAGE_KEY: Record<
  RoundingRuleType,
  'layerQuantityMessage' | 'palletQuantityMessage'
> = {
  layer: 'layerQuantityMessage',
  pallet: 'palletQuantityMessage',
}

/**
 * Picks the alert that names what moved the quantity, when the storefront has
 * worded one.
 *
 * A minimum raise names the minimum (`{{min}}`). A layer or pallet rounding
 * names its measure, and only when the committed quantity is a whole number of
 * it. The rounding texts take no number: the rule size and the committed
 * quantity are different numbers once more than one layer is involved, and a
 * single placeholder would invite the wrong one. Anything else, or any of these
 * with no text configured, returns `undefined` and the caller keeps its generic
 * message.
 *
 * Rounding messages apply only under `ROUND`. Under `VALIDATE` the same reason
 * is reported for a quantity that was left as it was, so "updated to a layer"
 * would be untrue there.
 *
 * When a quantity sits in both a layer and a pallet window, the counter reports
 * the pallet rule (it searches the rules from the last, and pallet is built
 * after layer), so the pallet message is the one shown.
 *
 * The counter's target must also be a multiple of `jumpQty`, so with a layer of
 * 10 and a jump of 4 a typed 8 is committed as 16, which is no layer at all; and
 * a round-down can land on 0, which empties the line. Neither is "updated to a
 * layer", so the generic message stays.
 */
export const resolveRuleQuantityMessage = ({
  reason,
  quantityAdjustment,
  ruleInvalidatedBy,
  minQty,
  committedValue,
  messages,
}: IResolveRuleQuantityMessage): string | undefined => {
  if (reason === QuantityAdjustmentReason.MIN_QTY) {
    return formatQuantityMessage(messages.minQuantityMessage, 'min', minQty ?? undefined)
  }
  if (
    reason !== QuantityAdjustmentReason.ROUNDING_RULE ||
    quantityAdjustment !== QuantityAdjustment.ROUND ||
    !isTypedRoundingRule(ruleInvalidatedBy)
  ) {
    return undefined
  }
  const { type, value } = ruleInvalidatedBy
  const landedOnRule = value > 0 && committedValue > 0 && committedValue % value === 0
  if (!landedOnRule) {
    return undefined
  }
  return configuredText(messages[RULE_MESSAGE_KEY[type]])
}
